import { useLocation } from '@reach/router'
import Fuse from 'fuse.js'
import React, { createElement, useState } from 'react'
import Button from './button'
import Container from './container'
import Grid from '../components/grid'

export function OfferHorizontal({
  kicker,
  heading,
  subheading,
  price,
  link,
  description
}) {
  return (
    <article className="rounded-xxx border-secondary flex flex-col border-2 md:flex-row">
      <div className="card-header-horizontal border-secondary flex flex-col items-center justify-center border-b-2 px-8 py-8 md:border-b-0 md:border-r-2">
        {price && (
          <div className="heading-2xl text-secondary mt-2 md:mt-4">
            ${price}
          </div>
        )}
        {link && (
          <Button className="mt-4 w-full" color="black" size="md" {...link} />
        )}
      </div>
      <div className="text-secondary col-span-12 p-6 md:col-span-9 md:p-6 lg:pr-16">
        {kicker && (
          <div
            className="accent-lg"
            dangerouslySetInnerHTML={{
              __html: kicker.replaceAll(
                /\*(?<em>.*?)\*/g,
                '<span class="emphasis">$<em></span>'
              )
            }}
          />
        )}
        {heading && <h3 class="heading-bs my-1 md:my-2">{heading}</h3>}
        {subheading && <div className="accent-bs">{subheading}</div>}
        {description && (
          <p className="body-bs 420:text-secondary exxxotica:text-tertiary mt-2 md:mt-4">{description}</p>
        )}
      </div>
    </article>
  )
}

export function OfferVertical({ heading, subheading, price, link, features }) {
  return (
    <article className="rounded-xxx border-secondary flex flex-col border-2 md:h-full">
      <div className="border-secondary flex flex-col border-b-2 px-8 py-8">
        {heading && <h3 className="heading-xs text-secondary">{heading}</h3>}
        {price && (
          <div className="heading-2xl text-secondary mt-2 md:mt-4">
            ${price}
          </div>
        )}
        {link && (
          <Button className="mt-4 w-full" color="black" size="md" {...link} />
        )}
      </div>
      <div className="text-secondary col-span-12 p-6 md:col-span-9 md:p-6 lg:pr-16">
        {subheading && <div className="control-bs">{subheading}</div>}
        {features?.length && (
          <ul className="420:text-secondary exxxotica:text-tertiary body-sm mt-2 leading-relaxed list-['•__'] pl-4">
            {features.map(({ description }, key) => (
              <li key={key}>{description}</li>
            ))}
          </ul>
        )}
      </div>
    </article>
  )
}

function capitalizeFirstLetter([first = '', ...rest]) {
  return [first.toUpperCase(), ...rest].join('')
}

export default function Pricing({ header, offers, design }) {
  function reset() {
    setCategory('')
  }

  const { heading, description, disclaimer } = header
  const { layout } = design

  let cards = [...(offers ?? [])]
  let locations = new Map()
  let categories = new Set()

  for (const offer of offers ?? []) {
    offer.location && locations.set(offer.location.slug, offer.location)
    offer.category && categories.add(offer.category)
  }

  // sort locations by date
  locations = new Map(
    [...locations].sort(([keyA, valueA], [keyB, valueB]) => {
      const dateA = valueA?.acf?.meta?.location?.dates?.[0]
      const dateB = valueB?.acf?.meta?.location?.dates?.[0]

      return dateA < dateB ? -1 : dateA > dateB ? 1 : 0
    })
  )
  locations = new Map([...locations].map(([key, value]) => [key, value.title]))
  categories = [...categories].sort()

  const [location, setLocation] = useState(
    new URLSearchParams(useLocation().search).get('location') ??
    (locations.size === 1 ? [...locations][0][0] : '')
  )
  const [category, setCategory] = useState('')

  const $and = []
  const fields = {
    category,
    'location.slug': location
  }

  for (const [key, value] of Object.entries(fields)) {
    value && $and.push({ [key]: `="${value}"` })
  }

  if ($and.length) {
    cards = new Fuse(cards, {
      keys: Object.keys(fields),
      useExtendedSearch: true
    })
      .search({ $and })
      .map(({ item }) => item)
  }

  return (
    <section className="bg-primary text-secondary">
      <div className="xxx-container-centered standard-spacing">
        {(heading || description) && (
          <Grid>
            <header className="col-span-12 py-16 text-center md:col-span-10 md:col-start-2 md:pt-16 md:pb-12 lg:col-span-8 lg:col-start-3">
              {heading && <h2 className="heading-3xl">{heading}</h2>}
              {description && <p className="body-lg mt-4">{description}</p>}
              {disclaimer && (
                <div className="font-body font-bs mt-4 font-medium uppercase">
                  {disclaimer}
                </div>
              )}
            </header>
          </Grid>
        )}
        {location ? (
          <>
            {(locations.size > 1 || categories.length > 1) && (
              <form className="text-secondary -mx-8 md:mx-0">
                <div className="md:rounded-xxx md:border-secondary md:flex md:justify-between md:border md:px-1">
                  <div className="scrollbar-hidden flex space-x-2 overflow-scroll px-8 md:block md:space-x-0 md:px-0">
                    {locations.size > 1 && (
                      <select
                        className="xxx-filter"
                        value={location}
                        onChange={({ target: { value } }) => {
                          setLocation(value)
                        }}>
                        {[...locations].map(([value, label], index) => (
                          <option key={index} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    )}
                    {categories.length > 1 && (
                      <select
                        className="xxx-filter"
                        value={category}
                        onChange={({ target: { value } }) => {
                          setCategory(value)
                        }}>
                        <option value="">Category</option>
                        {[...categories].map((category, index) => (
                          <option key={index}>{category}</option>
                        ))}
                      </select>
                    )}
                  </div>
                  <button
                    className="button button-sm button-dark hidden md:my-1 md:block"
                    type="button"
                    onClick={reset}>
                    Reset filters
                  </button>
                </div>
              </form>
            )}
            {cards?.length ? (
              <ul className="xxx-grid grid-price mt-12 mt-8 md:gap-8">
                {cards.map((card, key) => (
                  <li
                    key={key}
                    className={
                      {
                        vertical: 'mt-8 first:mt-0 md:mt-0',
                        horizontal: 'col-span-12'
                      }[layout]
                    }>
                    {createElement(
                      {
                        OfferHorizontal,
                        OfferVertical
                      }[`Offer${capitalizeFirstLetter(layout)}`],
                      card
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No results found.</p>
            )}
          </>
        ) : (
          <Container className="max-w-screen-xl">
            <h2 className="heading-xs">Select a location:</h2>
            <div className="mt-4 mb-16 flex flex-wrap gap-2" role="group">
              {[...locations].map(([value, label], index) => (
                <button
                  key={index}
                  className="xxx-button-group"
                  type="button"
                  onClick={() => {
                    setLocation(value)
                  }}>
                  {label}
                </button>
              ))}
            </div>
          </Container>
        )}
      </div>
    </section>
  )
}
